/**
 * Admin V3 — Compliance — Activity / Audit Log Viewer (Plan §2.2 M-F-16).
 * Phase 4 G5. Architecture V1 §DCA-5 layer 2 (driver_compliance_events).
 * G11.2 — Enriched event rendering (driver name + document + version + reason).
 * G11.4 — Motif workflow integration + driver filter client-side repair.
 *
 * G11.5 — Owner forensic verdict UI remediation (P1):
 *   - "Activity Journal filter text invisible": input now carries explicit
 *     `text-slate-900 placeholder:text-slate-400`. Without these, Tailwind
 *     preflight resolves the input color to `inherit`, which inherits from
 *     `body { color: hsl(var(--foreground)) }` set in index.css to a light
 *     foreground designed for the dark hero. On the admin panel's white
 *     background that meant light text on white = invisible.
 *   - "Activity Journal filtering not working": the filter logic was already
 *     correct (client-side substring match, G11.4). The user perceived it as
 *     "not working" because they could not see what they were typing. With
 *     text visibility restored the existing logic is verifiable.
 *   - "empty-state filter labels not properly rendered": label upgraded to
 *     text-sm/font-medium/slate-800 so it reads as a label, not decoration.
 *
 * Consumes:
 *   GET /admin/compliance/audit
 *
 * Read-only event-log viewer. Immutable events per Plan §2.6 M-A-4.
 */
import React, { useState, useMemo } from 'react';
import { Activity, Upload, CheckCircle2, XCircle, RotateCcw, User as UserIcon } from 'lucide-react';
import { SectionCard, EmptyState } from '../../shared/visualTokens';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import { t, DEFAULT_LOCALE } from '../../i18n';

// G11.5 — Defensive form-text classes for inputs/selects: override the
// global body-color inheritance trap (see file header).
const FORM_TEXT_CLS = 'text-slate-900 placeholder:text-slate-400';

// G11.2 — Per-event icon + colour mapping for at-a-glance scanning.
const EVENT_ICON = {
  document_uploaded:           { Icon: Upload,        cls: 'text-sky-600' },
  document_verified:           { Icon: CheckCircle2,  cls: 'text-emerald-600' },
  document_rejected:           { Icon: XCircle,       cls: 'text-rose-600' },
  document_renewal_requested:  { Icon: RotateCcw,     cls: 'text-amber-600' },
  driver_approved:             { Icon: CheckCircle2,  cls: 'text-emerald-700' },
  driver_rejected:             { Icon: XCircle,       cls: 'text-rose-700' },
  driver_suspended:            { Icon: XCircle,       cls: 'text-amber-700' },
  driver_reactivated:          { Icon: CheckCircle2,  cls: 'text-emerald-700' },
  driver_archived:             { Icon: XCircle,       cls: 'text-slate-600' },
};

const renderEventLabel = (eventType, locale) => {
  const i18nKey = `compliance.activity.event.${eventType}`;
  const translated = t(i18nKey, locale);
  // If the i18n lookup fell through (no translation found), it usually
  // returns the key itself — surface the raw event_type as a fallback so
  // we don't show "compliance.activity.event.foo" to the admin.
  return translated && translated !== i18nKey ? translated : eventType;
};

const renderDocumentCell = (e, locale) => {
  if (!e.document_type) return <span className="text-slate-400">—</span>;
  const docTypeLabel = t(`compliance.doctype.${e.document_type}`, locale);
  return (
    <div>
      <div className="text-slate-900">{docTypeLabel}</div>
      {e.document_version != null && (
        <div className="text-xs text-slate-500">v{e.document_version}</div>
      )}
    </div>
  );
};

const ComplianceActivityPage = () => {
  const locale = DEFAULT_LOCALE;
  // G11.4 — driverFilter is CLIENT-SIDE only; it does not drive the fetch.
  // The fetch is stable (limit-only), so the table never disappears mid-
  // keystroke. The filter applies substring matching across driver_name,
  // driver_email, and driver_id so the admin can type a name fragment
  // (e.g., "anzor"), an email fragment, or even a partial UUID.
  const [driverFilter, setDriverFilter] = useState('');
  const { data, loading, error } = useAdminQuery('/admin/compliance/audit', { limit: 100 });
  const allEvents = (data && data.events) || [];
  const events = useMemo(() => {
    const q = driverFilter.trim().toLowerCase();
    if (!q) return allEvents;
    return allEvents.filter((e) => {
      const haystack = [
        e.driver_name, e.driver_email, e.driver_id,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [allEvents, driverFilter]);

  return (
    <div className="p-6 space-y-6" data-testid="compliance-activity-page">
      <h1 className="text-[28px] font-semibold text-slate-900">
        {t('compliance.activity.title', locale)}
      </h1>

      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
        <div className="flex flex-col">
          <label htmlFor="activity-driver-filter" className="text-sm font-medium text-slate-800 mb-1">
            {t('compliance.activity.filter.driver', locale)}
          </label>
          <input
            id="activity-driver-filter"
            type="text"
            value={driverFilter}
            onChange={(e) => setDriverFilter(e.target.value)}
            placeholder={t('compliance.activity.filter.driver.placeholder', locale)}
            className={`px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-300 min-w-[260px] ${FORM_TEXT_CLS}`}
            data-testid="activity-driver-filter"
          />
        </div>
        {/* G11.4 — surface the filtered/total count so the admin sees the
            filter is working even when the substring matches everything.
            G11.5 — bumped to text-sm + slate-600 so the count is readable. */}
        <div className="text-sm text-slate-600 pb-2 font-medium">
          {events.length === allEvents.length
            ? `${allEvents.length} ${t('compliance.activity.count.events', locale)}`
            : `${events.length} / ${allEvents.length} ${t('compliance.activity.count.events', locale)}`}
        </div>
      </div>

      <SectionCard
        icon={Activity}
        iconClass="bg-violet-100 text-violet-700"
        title={t('compliance.activity.title', locale)}
      >
        {loading && <p className="text-slate-500 text-sm">{t('compliance.common.loading', locale)}</p>}
        {error && <p className="text-rose-700 text-sm">{t('compliance.common.error', locale)}</p>}
        {!loading && !error && events.length === 0 && (
          <EmptyState
            icon={Activity}
            title={t('compliance.activity.empty.title', locale)}
          />
        )}
        {!loading && !error && events.length > 0 && (
          <div className="overflow-x-auto" data-testid="compliance-activity-table">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-slate-600">
                  <th className="py-2 pr-4">{t('compliance.activity.table.timestamp', locale)}</th>
                  <th className="py-2 pr-4">{t('compliance.activity.column.driver', locale)}</th>
                  <th className="py-2 pr-4">{t('compliance.activity.column.document', locale)}</th>
                  <th className="py-2 pr-4">{t('compliance.activity.column.decision', locale)}</th>
                  <th className="py-2 pr-4">{t('compliance.activity.table.actor', locale)}</th>
                  <th className="py-2 pr-4">{t('compliance.activity.column.reason', locale)}</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => {
                  const ic = EVENT_ICON[e.event_type] || { Icon: Activity, cls: 'text-slate-500' };
                  const Icon = ic.Icon;
                  return (
                    <tr key={e.id} className="border-b border-slate-100" data-testid={`activity-row-${e.id}`}>
                      <td className="py-2 pr-4 text-slate-700 whitespace-nowrap">{e.timestamp || '—'}</td>
                      <td className="py-2 pr-4">
                        <div className="flex items-center gap-2">
                          <UserIcon className="h-3 w-3 text-slate-400" />
                          <div>
                            <div className="text-slate-900">{e.driver_name || e.driver_id}</div>
                            {e.driver_email && (
                              <div className="text-xs text-slate-500">{e.driver_email}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-2 pr-4">{renderDocumentCell(e, locale)}</td>
                      <td className="py-2 pr-4">
                        <div className="inline-flex items-center gap-2">
                          <Icon className={`h-4 w-4 ${ic.cls}`} />
                          <span className="text-slate-900">{renderEventLabel(e.event_type, locale)}</span>
                        </div>
                      </td>
                      <td className="py-2 pr-4 text-slate-700">
                        <div>{e.actor_id || '—'}</div>
                        {e.actor_role && <div className="text-xs text-slate-500">{e.actor_role}</div>}
                      </td>
                      <td className="py-2 pr-4">
                        {/* G11.4 — Motif workflow integration. When a reason
                            is present (typical for reject + renewal events)
                            it is now visually highlighted (italic + amber
                            background block) so the admin sees the WHY at a
                            glance and can correlate with the email the
                            driver received. Empty reasons remain "—" so
                            the column stays scan-friendly. */}
                        {e.reason ? (
                          <span
                            className="inline-block px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-900 italic text-xs max-w-xs"
                            title={e.reason}
                            data-testid={`activity-reason-${e.id}`}
                          >
                            « {e.reason} »
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
};

export default ComplianceActivityPage;
